// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The three still artefacts, and how a file reaches the person.
 *
 * The clip is made in a worker because a per-frame loop has to be; these are
 * one draw call each and are made on the main thread against the same renderer
 * that draws the live preview.
 */

import { ParallaxRenderer } from '../gl/renderer.ts';
import { encodeGrey16, depthToSamples } from './png16.ts';
import { coverUv, textureCap } from './plan.ts';
import { minMaxNormalise, type DepthField } from './depth.ts';

/**
 * The 16-bit depth PNG.
 *
 * Written from the normalised float field, straight to 65,536 levels — never
 * from the 8-bit image the pipeline also returns, which has already thrown the
 * precision away. White is near, which is the convention every depth-map
 * consumer expects, and it is linear in the network's own inverse depth.
 */
export async function depthPng(raw: Float32Array, width: number, height: number): Promise<Blob> {
  // Its OWN normalisation, over the true minimum and maximum. The percentile
  // normalisation the geometry uses clips a few per cent of the picture flat at
  // each end, which is right for deciding how far a camera may travel and wrong
  // for a file somebody is going to open in something else.
  const field = minMaxNormalise(raw, width, height);
  const { bytes } = await encodeGrey16(depthToSamples(field.data), field.width, field.height);
  return new Blob([bytes as BlobPart], { type: 'image/png' });
}

/**
 * Read a photograph in a way that is safe for both the network and the GPU.
 *
 * Two things go wrong here and neither announces itself.
 *
 * `imageOrientation` is passed explicitly because browsers have disagreed about
 * the default, and a phone photograph carries its rotation in EXIF rather than
 * in its pixels — get it wrong and the depth model and the renderer both see a
 * sideways picture, which is about as obviously broken as output gets.
 *
 * The size cap is for the stated hero input: a flatbed scan of an old print,
 * which comes off at 600 to 1200 dpi and so at six to twelve thousand pixels on
 * the long edge. `MAX_TEXTURE_SIZE` is commonly 8,192 and 4,096 on older
 * devices, and `texImage2D` past it does not throw — it sets INVALID_VALUE and
 * leaves a BLACK texture, so the failure arrives as a black clip with no error
 * anywhere.
 */
export async function readPhoto(file: Blob, maxEdge: number): Promise<ImageBitmap> {
  const probe = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const long = Math.max(probe.width, probe.height);
  if (long <= maxEdge) return probe;
  const scale = maxEdge / long;
  const resized = await createImageBitmap(probe, {
    imageOrientation: 'from-image',
    resizeWidth: Math.max(1, Math.round(probe.width * scale)),
    resizeHeight: Math.max(1, Math.round(probe.height * scale)),
    resizeQuality: 'high',
  });
  probe.close();
  return resized;
}

/** What this machine's driver will actually accept as a texture. */
export function maxTextureEdge(): number {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2');
  const limit = gl ? (gl.getParameter(gl.MAX_TEXTURE_SIZE) as number) : 4096;
  gl?.getExtension('WEBGL_lose_context')?.loseContext();
  return textureCap(limit);
}

async function canvasBlob(
  canvas: HTMLCanvasElement,
  type: 'image/png' | 'image/jpeg',
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('the browser could not encode the image'))),
      type,
      quality,
    );
  });
}

export interface StillOptions {
  readonly photo: ImageBitmap;
  readonly field: DepthField;
  readonly width: number;
  readonly height: number;
  readonly focus: number;
}

/**
 * A depth-graded defocus with the focal plane where the person put it.
 *
 * A separate canvas each time rather than reusing the preview's: the preview is
 * sized to the viewport and these are exported at full resolution.
 */
export async function bokehJpeg(options: StillOptions & { strengthPx: number }): Promise<Blob> {
  const canvas = document.createElement('canvas');
  const renderer = new ParallaxRenderer(canvas);
  try {
    if (!renderer.resize(options.width, options.height)) {
      throw new Error('the drawing surface could not be sized');
    }
    const cover = coverUv(options.photo.width, options.photo.height, options.width, options.height);
    renderer.setPhoto(options.photo);
    renderer.setDepth(options.field, cover);
    renderer.drawDepthOfField(cover, options.focus, options.strengthPx);
    return await canvasBlob(canvas, 'image/jpeg', 0.92);
  } finally {
    renderer.dispose();
  }
}

export async function anaglyphPng(options: StillOptions & { amplitude: number }): Promise<Blob> {
  const canvas = document.createElement('canvas');
  const renderer = new ParallaxRenderer(canvas);
  try {
    if (!renderer.resize(options.width, options.height)) {
      throw new Error('the drawing surface could not be sized');
    }
    const cover = coverUv(options.photo.width, options.photo.height, options.width, options.height);
    renderer.setPhoto(options.photo);
    renderer.setDepth(options.field, cover);
    renderer.drawAnaglyph(cover, options.focus, options.amplitude);
    return await canvasBlob(canvas, 'image/png');
  } finally {
    renderer.dispose();
  }
}

/** Hand a blob to the person. Object URLs are revoked on the next tick. */
export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** True when this browser can hand a file to another app. */
export function canShare(files: File[]): boolean {
  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  return typeof navigator.share === 'function' && (nav.canShare?.({ files }) ?? false);
}

export async function shareFile(file: File, title: string): Promise<boolean> {
  try {
    await navigator.share({ files: [file], title });
    return true;
  } catch (error) {
    // A cancelled share is not a failure and must not raise an error panel.
    if (error instanceof DOMException && error.name === 'AbortError') return false;
    throw error;
  }
}
